import { Component, Vue, Prop } from 'vue-property-decorator';
import { Helpers } from '../../common/Helpers';
import { DropdownForceOpenToward, DropdownItem } from '../../common/Types';
import { Alert, Icon, SvgIcon, Tag, TextInput } from '../../components';

@Component
export default class Dropdown extends Vue {

  @Prop({ required: true }) dropdownId!: string;
  @Prop() label!: string; // Label for the dropdown
  @Prop() value!: string; // Currently selected option
  @Prop() placeholder!: string;
  @Prop({ required: true }) options!: DropdownItem[]; // List of options to display
  @Prop() openIcon!: string; // Override the default arrow icons
  @Prop() closedIcon!: string; // Override the default arrow icons
  @Prop() disabled!: boolean; // Disable the dropdown
  @Prop() disabledClassName!: string; // Custom css for disabled div
  @Prop() stickyFooterDiv!: JSX.Element; // Custom div node for sticky footer
  @Prop() noSearchResultIcon!: string; // Icon name for the no search result case
  @Prop() dropDownButton!: boolean; // to change the view of dropdown input box as button
  @Prop() searchBox!: boolean; // Display a search box to filter the dropdown's options. Note - filtering logic must be provided in the searchOnChange method
  @Prop() highlightSelected!: boolean; // Highlight the currently selected option in the dropdown
  @Prop() showAssistiveTextAlways!: boolean; // show assistive text always
  @Prop() emptyOptionDisplay!: boolean; // enabled the empty message when no dropdown options are passed

  @Prop() assistiveText!: string; // Description text to display underneath the field
  @Prop() successMessage!: string; // Validation success message
  @Prop() errors!: string[]; // Validation error messages. If multiple, will display as a vertical list
  @Prop() emptyOptionsMessage!: string; // show empty message when no dropdown options are passed

  @Prop() className!: string; // Additional css class for the parent element
  @Prop() contentClassName!: string; // Additional css class for the option container
  @Prop() optionClassName!: string; // Additional css class for each option element
  @Prop() searchBarClassName!: string; // Additional css class for the search bar
  @Prop() inputClassName!: string; // Additonal css class for input box
  @Prop() forceOpenToward!: DropdownForceOpenToward; // Override the default logic for deciding whether to open the dropdown above or below the input.

  // Element flags
  @Prop() selectionOverride!: JSX.Element; // Provide an alternative element instead of the displayed input

  // multiselection
  @Prop({ default: false }) multiSelection!: boolean;
  @Prop() values!: string[];
  @Prop() removeFilterSelection!: (val: string) => void;

  @Prop() searchLoading!: boolean;
  @Prop() searchPlaceholder!: string;

  // State
  isOpen: boolean = false;
  dropdownUntouched: boolean = true;
  searchText: string = '';
  showFromIndex: number = 0;
  optionAdded: boolean = false;

  emptyDivClassName = 'tl-empty-tag';

  mounted(): void {
    document.addEventListener('mousedown', this._handleClickOutside);
    window.addEventListener('resize', Helpers.debounced(500, this._onResizeMultiSelection));
  }

  beforeDestroy(): void {
    document.removeEventListener('mousedown', this._handleClickOutside);
    window.removeEventListener('resize', Helpers.debounced(500, this._onResizeMultiSelection));
  }

  updated(): void {
    if (this.multiSelection) {
      if ((this.$refs.innerHeaderRef as any).current) {
        (this.$refs.innerHeaderRef as any).current.scrollLeft = (this.$refs.innerHeaderRef as any).current?.scrollWidth;
      }

      if (this.optionAdded) {
        // get the width of the tagcontainer
        const tagContainer = document.querySelector('.tl-tag-container') as HTMLElement;
        const widthOfTagContainer = tagContainer ? tagContainer.offsetWidth : 0;
        const tagHiddenCountEl = document.querySelector('.tl-tag-hidden-count') as HTMLElement;
        // 40px is considered for +x Tag in case it was not present in previous render
        const widthOfTagHiddenCountEl = tagHiddenCountEl ? tagHiddenCountEl.offsetWidth : 40;
        const tagHiddenCountStyles = tagHiddenCountEl ? getComputedStyle(tagHiddenCountEl) : null;
        const marginValueOfHiddenTag = tagHiddenCountStyles ? Number.parseInt(tagHiddenCountStyles.marginRight, 10) : 0;

        const containerWidth = widthOfTagContainer - widthOfTagHiddenCountEl - marginValueOfHiddenTag;

        // get the total length of the tags value
        let totalSizeOfTags: number = 0;
        let showFromIndex: number = this.showFromIndex;
        const optionAdded: boolean = false;

        const optionNodeList = document.getElementById(this.dropdownId)?.querySelectorAll('.tl-tag-option-selected');
        if (optionNodeList?.length) {
          /*
              Loop in the array of values from reverse and
              check if the width of Element exceeds the width of the remaining width of the element
              if yes return the count and remaining set of elements in array
          */
          for (let i = optionNodeList.length - 1; i >= 0; i--) {
            const tagContainer2: HTMLElement = optionNodeList[i] as HTMLElement;
            const styles: CSSStyleDeclaration = getComputedStyle(tagContainer);
            const width: number = tagContainer2.offsetWidth;
            const marginRight: number = Number.parseInt(styles.marginRight, 10);
            totalSizeOfTags += (width + marginRight);

            if (totalSizeOfTags > containerWidth) {
              // find the element
              const optionValue: string | null | undefined = tagContainer2.querySelector('.tl-tag-label')?.textContent;
              const indexOfOptionValue: number = this.values?.findIndex((selected: string) => selected.trim() === optionValue?.trim());
              showFromIndex = (indexOfOptionValue !== undefined && indexOfOptionValue !== -1) ? indexOfOptionValue + 1 : 0;
              break;
            }
          }
        }

        this.optionAdded = optionAdded;
        this.showFromIndex = showFromIndex;
      }
    }
  }

  render(): JSX.Element {
    let renderAbove: boolean = false;
    const parentElem: HTMLElement | null = document.getElementById(this.dropdownId);
    const searchHeight: number = 134;
    const optionHeight: number = Math.min(32 + this.options.length * 57, 314);
    const dropdownHeight: number = this.searchBox ? optionHeight + searchHeight : optionHeight;

    if (parentElem) {
      if (!this.forceOpenToward || this.forceOpenToward === 'auto') {
        renderAbove = window.innerHeight - parentElem.getBoundingClientRect().bottom < dropdownHeight;
      } else {
        renderAbove = this.forceOpenToward === 'up';
      }
    }

    return (
      <div id={`${this.dropdownId}-container`} class={`${this.className} tl-dropdown`}>
        {this._renderHeader()}
        <div id={this.dropdownId + '-dropdown-content'}
          class={`${this.optionClassName} tl-dropdown-options ${!this.isOpen && 'tl-dropdown-closed'} ${renderAbove && 'tl-dropdown-above'}`}>
          {this.searchBox && this._renderSearchBox()}
          <div id={this.dropdownId + '-dropdown-options'} class='tl-dropdown-options-list'>
            {this._renderOptions()}
          </div>
          {this.stickyFooterDiv ?
            this.stickyFooterDiv : ''}
        </div>
      </div>
    );
  }

  private _onResizeMultiSelection(): void {
    this.showFromIndex = 0;
    this.optionAdded = true;
  }

  private _generateSelectionList(showFromIndex: number): JSX.Element | JSX.Element[] | boolean {

    const finalArray = Array.isArray(this.values) && this.values.slice(showFromIndex).map((option: string) => {
      const currentEl: JSX.Element = <Tag key={option} label={option} className='tl-tag-margin-bottom-reset tl-content-center tl-tag-option-selected'
        onLabelClick={(e: any) => e.stopPropagation()}
        onKeyDown={(e: any) => this.$emit('tagKeyDown', e)}
        onCloseClick={(e: any) => {
          e.stopPropagation();
          this.$emit('removeFilterSelection', option);
          this.optionAdded = true;
          this.showFromIndex = 0;
        }}></Tag>;

      return currentEl;
    });

    return finalArray;

  }

  private _renderHeader(): JSX.Element {
    const openIcon: string = this.openIcon || 'icon-system-chevron-up';
    const closedIcon: string = this.closedIcon || 'icon-system-chevron-down';
    return <div id={this.dropdownId}
      class='tl-dropdown-header'
      onClick={this._toggleDropdown}
      onFocus={(e: any) => this.$emit('focus', e)}
      onBlur={(event: any) => {
        const headerComponent: HTMLElement | null = document.getElementById(this.dropdownId);
        const childInParent: boolean | undefined = headerComponent?.contains(event.relatedTarget as Node);
        // this is basically to re arrange the values array so that is show +x
        if (this.multiSelection && !childInParent) {
          this.optionAdded = true;
        }

        this.$emit('blur', event);
      }}
      onKeyDown={this._onKeyDown}
      onDoubleClick={(e: any) => this.$emit('dblClick', e)}
      onMouseOver={(e: any) => this.$emit('mouseOver', e)}
      onMouseOut={(e: any) => this.$emit('mouseOut', e)}
      tabIndex={0} >
      {this.label && !this.selectionOverride &&
        <div class={`tl-dropdown-label ${this.value || this.values?.length || this.placeholder ? 'tl-label-up' : ''}`}>
          {this.label}
        </div>
      }
      {
        this.selectionOverride ? this.selectionOverride :
          <div>
            {this.multiSelection ? (
              <div class={`tl-dropdown-display tl-content-center ${this.isOpen && 'dropdown-open'}`}>

                <div ref='innerHeaderRef' class='tl-tag-container'>
                  {this.showFromIndex && this.values.length ?
                    <Tag label={`+${this.showFromIndex}`}
                      onKeyDown={this._onTagKeyDown}
                      hideIcon={true}
                      onLabelClick={(e: any) => {
                        e.stopPropagation();
                        this.showFromIndex = 0;
                        this._focusTheHeader();
                      }}
                      class='tl-tag-margin-bottom-reset tl-tag-hidden-count' /> : null
                  }

                  {this._generateSelectionList(this.showFromIndex)}
                  <div class={`${this.emptyDivClassName}`}>4</div>

                </div>
              </div>) :

              <input class={`tl-dropdown-display ${this.inputClassName} ${this.isOpen && 'dropdown-open'}`}
                readOnly
                value={this.value}
                placeholder={this.placeholder}
                type='text'
                tabIndex={-1}
              />
            }
            {this._renderAssistiveText()}
          </div>
      }
      {!this.selectionOverride &&
        <SvgIcon name={this.isOpen ? openIcon : closedIcon} className='tl-dropdown-icon' />
      }
    </div>;
  }

  private _renderSearchBox(): JSX.Element {
    return <div class='tl-dropdown-search-container'>
      <TextInput
        id={`${this.dropdownId}-search`}
        className={`tl-dropdown-search ${this.searchBarClassName}`}
        placeholder={this.searchPlaceholder || 'Search'}
        value={this.searchText}
        onChange={this._searchOnChange}
        onFocus={(e: any) => this.$emit('searchOnFocus', e)}
        onBlur={(e: any) => this.$emit('searchOnBlur', e)}
        onClick={(e: any) => this.$emit('searchOnClick', e)}
        onKeyDown={this._onSearchKeyDown}
        tabIndex={0}
        loading={this.searchLoading}
        readonly={this.emptyOptionDisplay && this.options.length === 0 ? true : false}
      />
      {!this.searchLoading && <svg class='tl-dropdown-search-icon'><Icon name='icon-system-search' /></svg>}
    </div>;
  }

  private _searchOnChange(event: any): void {
    this.searchText = event.target.value;
    this.$emit('searchOnChange', event);
  }

  private _renderAssistiveText(): JSX.Element | JSX.Element[] | string {
    let content: JSX.Element | JSX.Element[] | string;
    if (this.errors && !this.isOpen) {
      content = this.errors.map((err, index) =>
        <Alert key={`err-${index}`}
          name='alert-inline-warning'
          icon='icon-system-alert-small'
          label={err}
          labelClass='error-message body14-light' />,
      );
    } else if (this.assistiveText && (this.showAssistiveTextAlways || (this.isOpen || this.dropdownUntouched))) {
      content = <Alert name='alert-inline-info-tick' label={this.assistiveText} labelClass='body14-light' />;
    } else if (this.successMessage) {
      content = <Alert name='alert-inline-success' icon='icon-system-tick-copy' label={this.successMessage} labelClass='success-message body14-light' />;
    } else {
      content = '';
    }
    return (
      <div class='tl-dropdown-assistive-text'>{content}</div>
    );
  }

  private _highlightMatch(label: string, itemId: string): JSX.Element {
    const match: RegExpMatchArray | null = label.toLowerCase().match(this.searchText.toLowerCase());
    if (match != null && match.index !== undefined) {
      const start: string = label.substring(0, match.index);
      const matchedText: string = label.substring(match.index, match.index + this.searchText.length);
      const end: string = label.substring(match.index + this.searchText.length);
      return (
        <span id={`${itemId}-text`} data-item={label}>{start}
          <span id={`${itemId}-highlight`} class='tl-dropdown-match' data-item={label}>
            {matchedText}
          </span>
          {end}
        </span>
      );
    } else {
      return <span id={`${itemId}-text`} data-item={label}>{label}</span>;
    }
  }

  private _renderOptions(): JSX.Element | JSX.Element[] {
    if (this.options.length === 0) {
      return <div class={`tl-dropdown-disabled-option test ${this.disabledClassName}`}>
        <SvgIcon name={`icon-system-alert-small`} className='tl-inline-alert-icon' />
        {this.emptyOptionDisplay ? this.emptyOptionsMessage : 'There are no results'}
      </div>;
    }
    return this.options.map((option) => {
      const optionSelected = this.highlightSelected && this.value === option.displayText;
      if (option.disabled) {
        return <div class={`tl-dropdown-disabled-option ${this.disabledClassName}`} key={option.itemId}
          id={option.itemId}>
          {this.noSearchResultIcon ?
            <SvgIcon name={`${option.iconName}`} className='tl-inline-alert-icon' /> : ''}
          {option.displayText}</div>;
      } else if (option.subheader) {
        return <div class='tl-dropdown-subheader-option' key={option.itemId}
          id={option.itemId}>{option.displayText}</div>;
      } else {
        return <div class={`tl-dropdown-option ${this.optionClassName} ${optionSelected ? `tl-option-selected` : ''}`}
          key={`${this.dropdownId}-${option.itemId}`}
          id={`${option.itemId}`}
          onClick={this._onOptionClick}
          onKeyDown={this._onListKeyDown}
          tabIndex={-1}
          data-item={option.itemId}
          data-optionid={option.optionId}
        >
          {this._highlightMatch(option.displayText, option.itemId)}
        </div>;
      }
    });
  }

  private _handleClickOutside(event: any): void {
    const parentElem: HTMLElement | null = document.getElementById(`${this.dropdownId}-container`);
    if (parentElem && !parentElem.contains(event.target)) {
      this._closeDropdown();
    }
  }

  private _onOptionClick(event: any): void {
    if (this.$listeners.optionClick) {
      const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);

      if (inputElem !== null) {
        inputElem.focus();
      }
      this.$emit('optionClick', event);
    }
    if (this.multiSelection) {
      this.optionAdded = true;
    } else {
      this._closeDropdown();
    }
  }

  private _onSearchKeyDown(event: any): void {
    const dropdownContent: HTMLElement | null = document.getElementById(this.dropdownId + '-dropdown-options');
    if (event.key === 'ArrowDown' && dropdownContent !== null) {
      const children: HTMLCollection = dropdownContent.children;
      for (const child of children) {
        if (child.getAttribute('tabindex') === '-1') {
          const childElement: HTMLElement | null = document.getElementById(child.id);
          if (childElement !== null) {
            childElement.focus();
          }
          break;
        }
      }
    }
    if (event.key === 'ArrowUp') {
      const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
      if (inputElem !== null) {
        inputElem.focus();
      }
    }

    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
      if (inputElem !== null) {
        inputElem.focus();
      }
      this._closeDropdown();
    }

    if (event.key === 'Tab') {
      this._closeDropdown();
    }
    this.$emit('searchOnKeyPress', event);
  }

  private _onListKeyDown(event: any): void {
    // Enter or space selects an option and returns focus to the textbox
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Space') {
      this._onOptionClick(event);
      const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
      if (inputElem !== null) {
        inputElem.focus();
      }
    }
    // Arrow keys navigate through selectable options
    // Up arrow on first option returns focus to the textbox (but doesn't close list)
    if (event.key === 'ArrowDown') {
      let targetElem: any = event.target.nextSibling;
      while (targetElem && targetElem.getAttribute('tabindex') !== '-1') {
        targetElem = targetElem.nextSibling;
      }
      if (targetElem !== null) {
        targetElem.focus();
      }
    }
    if (event.key === 'ArrowUp') {
      let targetElem: any = event.target.previousSibling;
      while (targetElem && targetElem.getAttribute('tabindex') !== '-1') {
        targetElem = targetElem.previousSibling;
      }
      if (targetElem !== null) {
        targetElem.focus();
      }
      if (targetElem === null) {
        if (this.searchBox) {
          const searchElem: HTMLElement | null = document.getElementById(`${this.dropdownId}-search`);
          if (searchElem !== null) {
            searchElem.focus();
          }
        } else {
          const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
          if (inputElem !== null) {
            inputElem.focus();
          }
        }
      }
    }

    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
      if (inputElem !== null) {
        inputElem.focus();
      }
      this._closeDropdown();
    }

    // When we tab out, close the dropdown
    if (event.key === 'Tab') {
      this._closeDropdown();
    }
  }

  private _onKeyDown(event: any): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Space') {
      this._toggleDropdown();
    }

    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
      if (this.isOpen) {
        const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
        if (inputElem !== null) {
          inputElem.focus();
        }
        this._closeDropdown();
      }
    }

    const dropdownContent: HTMLElement | null = document.getElementById(this.dropdownId + '-dropdown-options');
    if (event.key === 'ArrowDown' && dropdownContent !== null) {
      if (this.searchBox) {
        const searchElem: HTMLElement | null = document.getElementById(`${this.dropdownId}-search`);
        if (searchElem !== null) {
          searchElem.focus();
        }
      } else {
        const children: HTMLCollection = dropdownContent.children;
        for (const child of children) {
          if (child.getAttribute('tabindex') === '-1') {
            const childElement: HTMLElement | null = document.getElementById(child.id);
            if (childElement !== null) {
              childElement.focus();
            }
            break;
          }
        }
      }

    }

    const tagList: NodeListOf<Element> | undefined = document.getElementById(this.dropdownId)?.querySelectorAll('.tl-tag-value');
    if (event.key === 'ArrowRight' && tagList?.length) {
      const firstElement: HTMLElement = tagList[0] as HTMLElement;
      this._assignFocusTo(firstElement);
    }

    if (event.keyCode === 37 && tagList?.length) {
      const lastElement: HTMLElement = tagList[tagList.length - 1] as HTMLElement;
      if (lastElement !== null) {
        lastElement.focus();
      }
    }

    // When we tab out, close the dropdown
    if (event.key === 'Tab') {
      this._closeDropdown();
    }

    if (this.$listeners.onKeyDown) {
      this._onKeyDown(event);
    }
  }

  private _onTagKeyDown(event: any): void {
    event.stopPropagation();
    const tagList: NodeListOf<Element> | undefined = document.getElementById(this.dropdownId)?.querySelectorAll('.tl-tag-value');

    if (event.keyCode && tagList?.length) {
      const tagElement: any = event.target;
      const itemId: any = tagElement.firstElementChild.innerHTML;
      let targetElem: any;
      switch (event.keyCode) {
        // Left Key
        case 37:
          targetElem = event.target.previousSibling;
          targetElem ? targetElem.focus() : this._focusTheHeader();

          break;

        // Right Key
        case 39:
          targetElem = event.target.nextSibling;
          targetElem && !targetElem.classList[0].includes(this.emptyDivClassName) ?
            targetElem.focus() : this._focusTheHeader();
          break;

        // BackSpace Key
        case 8:
          if (this.removeFilterSelection) {
            this.removeFilterSelection(itemId);
          }
          this.optionAdded = true;
          this.showFromIndex = 0;

          // if any last element is left then add focus to it
          const tagList2: NodeListOf<Element> | undefined = document.getElementById(this.dropdownId)?.querySelectorAll('.tl-tag-value');
          const lastElement: HTMLElement | undefined = tagList && tagList[tagList2!.length - 1] as HTMLElement;
          const withoutCross: boolean | undefined = lastElement?.firstElementChild?.innerHTML.trim().includes('+');
          if (lastElement && !withoutCross) {
            lastElement.focus();
          } else {
            this._focusTheHeader();
          }

          break;

        // Enter Key
        case 13:
          if (itemId.trim().includes('+')) {
            this.showFromIndex = 0;
            this._focusTheHeader();
          }

          break;

        // tab key
        // case 9:
        //     this.focusTheHeader()

        default:
          break;
      }
    }

  }

  private _toggleDropdown(): void {
    if (this.isOpen) {
      this._closeDropdown();
    } else {
      this.isOpen = true;
      this.dropdownUntouched = false;
    }
  }

  private _closeDropdown(): void {
    if (this.isOpen) {
      const searchText: string = '';
      const event: any = { target: { value: searchText } };

      if (this.$listeners.searchOnChange) {
        this.$emit('searchOnChange', event);
      }

      this.isOpen = false;
      this.searchText = searchText;
    }
  }

  private _focusTheHeader(): void {
    const inputElem: HTMLElement | null = document.getElementById(this.dropdownId);
    if (inputElem) {
      inputElem.focus();
    }
  }

  private _assignFocusTo(element: HTMLElement | null): void {
    if (element) {
      element.focus();
    }
  }

}
